import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { API } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { FolderKanban, ListChecks, CheckCircle2, Timer, Users, Download, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import StatusPieChart from '../components/Charts/StatusPieChart';
import { useAuth } from '../lib/auth';

function fmtHours(s) { return `${(s / 3600).toFixed(1)}h`; }

export default function Reports() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState([]);
  const [progress, setProgress] = useState([]);
  const [summary, setSummary] = useState(null);

  const [myTasks, setMyTasks] = useState([]);
  const [myTimeEntries, setMyTimeEntries] = useState([]);

  const downloadCsv = async (path) => {
    try {
      const t = localStorage.getItem('tm_jwt');
      const res = await fetch(`${API}${path}`, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = path.split('/').pop() || 'export.csv';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert(`Export failed: ${e.message}`); }
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    doc.setFontSize(18); doc.text('BexConnect — Team Report', 40, 40);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Generated ${new Date().toLocaleString()}`, 40, 58);
    if (summary) {
      doc.setTextColor(0); doc.setFontSize(11);
      doc.text([
        `Projects: ${summary.total_projects}`,
        `Tasks: ${summary.total_tasks}`,
        `Completed: ${summary.completed_tasks}`,
        `Time tracked: ${(summary.total_seconds / 3600).toFixed(1)}h`,
        `Active members: ${summary.active_members}`,
      ].join('   ·   '), 40, 82);
    }
    // Project progress table
    autoTable(doc, {
      startY: 110,
      head: [['Project', 'Completed / Total', 'Progress']],
      body: progress.map((p) => [p.name, `${p.completed_tasks}/${p.total_tasks}`, `${p.pct_complete}%`]),
      styles: { fontSize: 10 }, headStyles: { fillColor: [0, 121, 191] },
    });
    // Member stats
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Member', 'Role', 'Assigned', 'Completed', 'Hours']],
      body: stats.map((s) => [s.full_name || s.email, s.role, s.tasks_assigned, s.tasks_completed, (Number(s.total_seconds) / 3600).toFixed(1)]),
      styles: { fontSize: 10 }, headStyles: { fillColor: [0, 121, 191] },
    });
    doc.save(`task-matrix-report-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  useEffect(() => {
    (async () => {
      const [s, p, m, t, te] = await Promise.all([
        api.get('/reports/member-stats'),
        api.get('/reports/project-progress'),
        api.get('/reports/summary'),
        api.get('/tasks'),
        api.get('/timer/entries?scope=self')
      ]);
      setStats(s.data.stats);
      setProgress(p.data.progress);
      setSummary(m.data.summary);

      if (user) {
        const assigned = (t.data.tasks || []).filter(task => task.assignees?.some(a => a.id === user.id) || task.assignee_id === user.id);
        setMyTasks(assigned);
        setMyTimeEntries(te.data.entries || []);
      }
    })();
  }, [user]);

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="reports-page">
      <div style={{ color: 'var(--trello-text)', marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Reports & Analytics</h1>
          <p style={{ opacity: 0.85, marginTop: 4 }}>Team performance at a glance</p>
        </div>
        <div className="flex gap-2">
          <button className="tm-btn-ghost" data-testid="export-pdf"
            onClick={exportPdf} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--trello-border)', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600 }}>
            <FileText size={14} />
            PDF report
          </button>
          <button className="tm-btn-ghost" data-testid="export-members-csv"
            onClick={() => downloadCsv('/reports/export/member-stats.csv')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--trello-border)', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600 }}>
            <Download size={14} />
            Member stats CSV
          </button>
          <button className="tm-btn-ghost" data-testid="export-projects-csv"
            onClick={() => downloadCsv('/reports/export/projects.csv')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--trello-border)', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600 }}>
            <Download size={14} />
            Projects CSV
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="reports-summary">
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-projects"
            style={{ '--tint': '#0079BF', '--tint-bg': '#E4F0F6' }} onClick={() => nav('/projects')}>
            <div className="flex items-center gap-2 mb-1"><FolderKanban size={16} /><span className="tm-stat-label">Projects</span></div>
            <div className="tm-stat-value">{summary.total_projects}</div>
          </button>
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-tasks"
            style={{ '--tint': '#D29034', '--tint-bg': '#FFF7E6' }} onClick={() => nav('/command-center')}>
            <div className="flex items-center gap-2 mb-1"><ListChecks size={16} /><span className="tm-stat-label">Tasks</span></div>
            <div className="tm-stat-value">{summary.total_tasks}</div>
          </button>
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-completed"
            style={{ '--tint': '#519839', '--tint-bg': '#E6F4EA' }} onClick={() => nav('/command-center?status=done')}>
            <div className="flex items-center gap-2 mb-1"><CheckCircle2 size={16} /><span className="tm-stat-label">Completed</span></div>
            <div className="tm-stat-value">{summary.completed_tasks}</div>
          </button>
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-time"
            style={{ '--tint': '#89609E', '--tint-bg': '#F3ECF7' }} onClick={() => document.getElementById('member-stats-table')?.scrollIntoView({ behavior: 'smooth' })}>
            <div className="flex items-center gap-2 mb-1"><Timer size={16} /><span className="tm-stat-label">Time Tracked</span></div>
            <div className="tm-stat-value">{fmtHours(summary.total_seconds)}</div>
          </button>
          <button type="button" className="tm-stat-tinted" data-testid="rep-card-active"
            style={{ '--tint': '#B04632', '--tint-bg': '#FBEDEA' }} onClick={() => nav('/team')}>
            <div className="flex items-center gap-2 mb-1"><Users size={16} /><span className="tm-stat-label">Active</span></div>
            <div className="tm-stat-value">{summary.active_members}</div>
          </button>
        </div>
      )}
      {/* My Performance Dashboard */}
      {user && (
        <div className="tm-stat-card mb-6" style={{ borderLeft: '4px solid #519839', background: 'var(--trello-list-bg)', padding: '16px' }} data-testid="personal-dashboard">
          <div className="flex justify-between items-center mb-3">
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle2 size={16} className="text-emerald-600" />
              My Performance Dashboard · {user.full_name || 'Teammate'}
            </h2>
            <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full">
              Active Teammate
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card 1: Completion Burndown */}
            <div className="bg-white p-3 rounded-lg border border-slate-100 flex flex-col justify-between">
              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Burndown Rate</span>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-xl font-black text-slate-800">
                    {myTasks.filter(t => t.completed).length}/{myTasks.length}
                  </span>
                  <span className="text-[10px] text-slate-500 font-medium">tasks finished</span>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-[10px] font-bold text-slate-650 mb-1">
                  <span>Task Burndown Progress</span>
                  <span>{myTasks.length > 0 ? Math.round((myTasks.filter(t => t.completed).length / myTasks.length) * 100) : 0}%</span>
                </div>
                <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                  <div
                    className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                    style={{ width: `${myTasks.length > 0 ? (myTasks.filter(t => t.completed).length / myTasks.length) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Card 2: Personal Hours Tracked */}
            <div className="bg-white p-3 rounded-lg border border-slate-100 flex flex-col justify-between">
              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Hours Tracked</span>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-xl font-black text-slate-800">
                    {(myTimeEntries.reduce((acc, e) => acc + (e.duration_seconds || 0), 0) / 3600).toFixed(1)}h
                  </span>
                  <span className="text-[10px] text-slate-500 font-medium">this week</span>
                </div>
              </div>
              <p className="text-[9px] text-slate-400 mt-2 leading-none">
                Includes {myTimeEntries.filter(e => e.status === 'pending').length} pending, {myTimeEntries.filter(e => e.status === 'approved').length} approved.
              </p>
            </div>

            {/* Card 3: Recent Time Logs */}
            <div className="bg-white p-3 rounded-lg border border-slate-100 flex flex-col justify-between">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Recent Time Entries</span>
              <div className="space-y-1.5 max-h-[85px] overflow-y-auto pr-1">
                {myTimeEntries.length === 0 ? (
                  <span className="text-[10px] text-slate-400 italic">No logged hours yet</span>
                ) : (
                  myTimeEntries.slice(0, 3).map((e, idx) => (
                    <div key={idx} className="flex justify-between items-center text-[10px] border-b border-slate-50 pb-1 last:border-0 last:pb-0">
                      <span className="font-semibold text-slate-700 truncate max-w-[120px]">{e.task_title || 'Unnamed Task'}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-slate-500 font-bold">{(e.duration_seconds / 3600).toFixed(1)}h</span>
                        <span className={`px-1 py-0.2 rounded-full text-[8px] font-bold capitalize ${e.status === 'approved' ? 'bg-emerald-55 text-emerald-700' : e.status === 'rejected' ? 'bg-red-55 text-red-700' : 'bg-amber-55 text-amber-700'}`}>
                          {e.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Project progress</h3>
          {progress.length === 0 && <p style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No projects yet.</p>}
          <div className="space-y-3">
            {progress.map(p => (
              <Link key={p.project_id} to={`/projects/${p.project_id}`} data-testid={`progress-${p.project_id}`}
                style={{ display: 'block', textDecoration: 'none', color: 'inherit', padding: '6px', borderRadius: 6 }}
                className="hover:bg-slate-50">
                <div className="flex justify-between mb-1" style={{ fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{p.name}</span>
                  <span>{p.completed_tasks}/{p.total_tasks} · {p.pct_complete}%</span>
                </div>
                <div className="tm-progress-track"><div className="tm-progress-fill" style={{ width: `${p.pct_complete}%`, background: p.color || '#0079BF' }} /></div>
              </Link>
            ))}
          </div>
        </div>

        <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Task completion by member</h3>
          {stats.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.map(s => ({ name: (s.full_name || '').split(' ')[0], completed: Number(s.tasks_completed), assigned: Number(s.tasks_assigned), color: s.avatar_color || '#0079BF' }))}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="completed" radius={[4, 4, 0, 0]}>{stats.map((s, i) => <Cell key={i} fill={s.avatar_color || '#0079BF'} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="tm-stat-card mt-4" style={{ borderLeft: 'none' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Workspace Task Distribution</h3>
        {summary ? (
          <StatusPieChart completed={summary.completed_tasks} total={summary.total_tasks} />
        ) : (
          <p style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>Loading...</p>
        )}
      </div>

      <div className="tm-stat-card mt-4" style={{ borderLeft: 'none' }} id="member-stats-table">
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Member stats</h3>
        <table style={{ width: '100%', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--trello-text-muted)', fontSize: 12, textTransform: 'uppercase' }}>
              <th style={{ padding: '6px 8px' }}>Member</th><th>Role</th><th>Assigned</th><th>Completed</th><th>Time</th>
            </tr>
          </thead>
          <tbody>
            {stats.map(s => (
              <tr key={s.user_id} data-testid={`stat-row-${s.user_id}`}
                onClick={() => nav(`/command-center?assignee=${s.user_id}`)}
                style={{ borderTop: '1px solid var(--trello-border)', cursor: 'pointer' }}
                className="hover:bg-slate-50">
                <td style={{ padding: '10px 8px' }}>
                  <div className="flex items-center gap-2">
                    <div className="tm-avatar" style={{ width: 26, height: 26, fontSize: 11, background: s.avatar_color || 'var(--trello-text-muted)' }}>{(s.full_name || s.email).slice(0, 2).toUpperCase()}</div>
                    {s.full_name}
                  </div>
                </td>
                <td><span className={`tm-badge tm-badge-${s.role}`}>{s.role}</span></td>
                <td>{s.tasks_assigned}</td>
                <td>{s.tasks_completed}</td>
                <td>{fmtHours(s.total_seconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
